import { useState, useEffect, useCallback, useRef } from 'react';
import { useTenant } from '../context/TenantContext';

const POLL_MS = 30 * 1000; // alle 30s nach neuen Notifications schauen

// Floating-Bell oben rechts. Sichtbar überall, wo der User einen aktiven Mandanten
// hat. Per Polling — kein SSE, weil das für In-App-Benachrichtigungen overkill
// wäre und zusätzliche Connections kostet. Klick → Dropdown-Popover.
export default function NotificationBell({ onNavigateTask }) {
  const { tenantFetch, activeTenant } = useTenant();
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const refreshCount = useCallback(async () => {
    if (!activeTenant) return;
    try {
      const res = await tenantFetch('/api/notifications/unread-count');
      const data = await res.json();
      if (res.ok) setUnreadCount(data.count || 0);
    } catch { /* ignore — Polling versucht's gleich wieder */ }
  }, [tenantFetch, activeTenant]);

  const loadList = useCallback(async () => {
    if (!activeTenant) return;
    setLoading(true);
    try {
      const res = await tenantFetch('/api/notifications?limit=30');
      const data = await res.json();
      if (res.ok) setItems(data.notifications || []);
    } finally {
      setLoading(false);
    }
  }, [tenantFetch, activeTenant]);

  // Initial + Polling
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  // Click-outside zum Schließen
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Beim Öffnen die volle Liste laden (nicht beim Polling — dort nur Count)
  const toggle = () => {
    if (!open) loadList();
    setOpen(v => !v);
  };

  const markRead = async (id) => {
    setItems(prev => prev.map(n => n._id === id ? { ...n, readAt: new Date() } : n));
    setUnreadCount(c => Math.max(0, c - 1));
    try {
      await tenantFetch(`/api/notifications/${id}/read`, { method: 'PATCH' });
    } catch { /* optimistic — egal */ }
  };

  const markAll = async () => {
    setItems(prev => prev.map(n => n.readAt ? n : { ...n, readAt: new Date() }));
    setUnreadCount(0);
    try {
      await tenantFetch('/api/notifications/read-all', { method: 'PATCH' });
    } catch { /* optimistic */ }
  };

  const clickNotif = async (n) => {
    if (!n.readAt) await markRead(n._id);
    setOpen(false);
    if (n.link?.type === 'task' && onNavigateTask) {
      onNavigateTask(n.link.id);
    }
  };

  if (!activeTenant) return null; // ohne Mandant keine Notifications

  return (
    <div className="notification-bell-wrap" ref={wrapRef}>
      <button
        className="notification-bell-btn"
        onClick={toggle}
        title={unreadCount > 0 ? `${unreadCount} ungelesene Benachrichtigung(en)` : 'Benachrichtigungen'}
        aria-label="Benachrichtigungen"
      >
        <span className="notification-bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
      {open && (
        <div className="notification-popover">
          <div className="notification-popover-head">
            <span>Benachrichtigungen</span>
            {items.some(n => !n.readAt) && (
              <button className="link-btn" onClick={markAll}>Alle als gelesen markieren</button>
            )}
          </div>
          <div className="notification-popover-body">
            {loading && <p className="section-hint" style={{ padding: 12 }}>Lädt…</p>}
            {!loading && items.length === 0 && (
              <p className="section-hint" style={{ padding: 16, textAlign: 'center' }}>
                Keine Benachrichtigungen.
              </p>
            )}
            {!loading && items.map(n => (
              <button
                key={n._id}
                className={`notification-item ${n.readAt ? '' : 'unread'}`}
                onClick={() => clickNotif(n)}
              >
                <span className="notification-item-icon">
                  {n.type === 'task_assigned' ? '📋' : '🔔'}
                </span>
                <div className="notification-item-text">
                  <div className="notification-item-title">{n.title}</div>
                  <div className="notification-item-body">{n.body}</div>
                  <div className="notification-item-time">{relativeTime(n.createdAt)}</div>
                </div>
                {!n.readAt && <span className="notification-item-dot" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'gerade eben';
  const m = Math.floor(s / 60);
  if (m < 60) return `vor ${m} Min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} Tag${d === 1 ? '' : 'en'}`;
  return new Date(iso).toLocaleDateString('de-DE');
}
